import Label from "@components/Label";
import { LockedMenuButton } from "@components/lockedButtons/LockedMenuButton";
import useEnabledModules from "@hooks/useEnabledModules";
import useLocales from "@hooks/useLocales";
import usePermissions from "@hooks/usePermissions";
import {
  Divider,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { statusLabelMap } from "@pages/procurement/config/prStatusFilterOptions";
import { PATH_PROCUREMENT } from "@routes/paths";
import { fDate, fDateTime } from "@utils/formatTime";
import { Check, FileText, Pencil, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Permissions } from "src/@types/permissions";
import ConfirmDialog from "src/components/ConfirmDialog";
import { TableMoreMenu } from "src/components/table";
import {
  IPurchaseRequisition,
  PurchaseRequisitionStatus,
} from "../../types/purchaseRequisitionTypes";

interface ProcurementTableRowProps {
  row: IPurchaseRequisition;
  isDraft?: boolean;
  onDelete: () => Promise<void>;
  onPost: () => Promise<void>;
}

export const ProcurementTableRow: React.FC<ProcurementTableRowProps> = (
  props,
) => {
  const { row, isDraft = false, onDelete, onPost } = props;
  const {
    createdDate,
    departmentDimension,
    documentNumber,
    dueDeliveryDate,
    id,
    requesterName,
    status,
    updatedDate,
    vendorName,
  } = row;
  const { permissionHandle } = usePermissions();
  const { translate } = useLocales();
  const { palette } = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { moduleHandle } = useEnabledModules();
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const isPostedOrDiscarded =
    status === PurchaseRequisitionStatus.Posted ||
    status === PurchaseRequisitionStatus.Discard;
  const isDrafted = status === PurchaseRequisitionStatus.Draft;
  const [openConfirmFinalize, setOpenConfirmFinalize] = useState(false);
  const [finalizeLoading, setFinalizeLoading] = useState(false);

  const hasPRCreatePermission = permissionHandle([
    Permissions.Procurement_PurchaseRequisition_Create,
  ]);
  const hasPRUpdatePermission = permissionHandle([
    Permissions.Procurement_PurchaseRequisition_Update,
  ]);
  const hasPRDeletePermission = permissionHandle([
    Permissions.Procurement_PurchaseRequisition_Delete,
  ]);
  const hasPRFinalizePermission = permissionHandle([
    Permissions.Procurement_PurchaseRequisition_Post,
  ]);

  const hasPOCreatePermission = permissionHandle([
    Permissions.Procurement_PurchaseOrder_Create,
  ]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleUpdate = () => {
    navigate(
      `${
        PATH_PROCUREMENT.general.purchaseRequisition.general
          .purchaseRequisitionCreate
      }?${isDrafted ? "draftDocId" : "purchaseRequisitionId"}=${id}`,
    );
    handleCloseMenu();
  };

  const handleOpenConfirmDelete = () => {
    setOpenConfirmDelete(true);
    handleCloseMenu();
  };

  const handleCloseConfirmDelete = () => {
    setOpenConfirmDelete(false);
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    await onDelete();
    setDeleteLoading(false);
    setOpenConfirmDelete(false);
  };

  const handleCreatePOFromPR = () => {
    navigate(
      `${PATH_PROCUREMENT.general.purchaseOrder.general.purchaseOrderCreate}?purchaseRequisitionId=${row?.id}`,
    );
  };

  const handleOpenConfirmFinalize = () => {
    setOpenConfirmFinalize(true);
    handleCloseMenu();
  };

  const handleCloseConfirmFinalize = () => {
    setOpenConfirmFinalize(false);
  };

  const handleConfirmFinalize = async () => {
    setFinalizeLoading(true);
    await onPost();
    setFinalizeLoading(false);
    setOpenConfirmFinalize(false);
  };

  return (
    <TableRow>
      <TableCell align="left">
        <span>{documentNumber ?? "-"}</span>
      </TableCell>

      <TableCell align="left">{requesterName ?? "-"}</TableCell>
      <TableCell align="left">
        <Label
          variant="ghost"
          color={statusLabelMap[status].color}
          sx={{ textTransform: "capitalize" }}
        >
          {translate(statusLabelMap[status].translationKey)}
        </Label>
      </TableCell>
      <TableCell align="left">{vendorName ?? "-"}</TableCell>
      <TableCell align="left">{departmentDimension ?? "-"}</TableCell>
      <TableCell align="left">
        {createdDate ? fDateTime(createdDate) : "-"}
        {updatedDate && (
          <Typography variant="body2" color={palette.text.label}>
            {`${translate("last_updated")} ${fDateTime(updatedDate)}`}
          </Typography>
        )}
      </TableCell>
      <TableCell align="left">
        {dueDeliveryDate ? fDate(dueDeliveryDate) : "-"}
      </TableCell>
      <TableCell align="right">
        <TableMoreMenu
          open={anchorEl}
          onOpen={handleOpenMenu}
          onClose={handleCloseMenu}
          actions={
            <>
              <LockedMenuButton
                title={translate("post")}
                onClick={handleOpenConfirmFinalize}
                locked={!hasPRFinalizePermission}
                disabled={isPostedOrDiscarded || isDrafted}
                startIcon={<Check />}
                module={"Procurement"}
                moduleLocked={!moduleHandle(["Procurement"])}
              />
              <LockedMenuButton
                disabled={isPostedOrDiscarded}
                title={`${translate("edit")} ${translate(
                  isDrafted ? "draft" : "",
                )}`}
                onClick={handleUpdate}
                locked={
                  isDrafted ? !hasPRCreatePermission : !hasPRUpdatePermission
                }
                startIcon={<Pencil />}
                module={"Procurement"}
                moduleLocked={!moduleHandle(["Procurement"])}
              />
              <LockedMenuButton
                title={translate("create_po_from_pr")}
                disabled={!isPostedOrDiscarded}
                onClick={handleCreatePOFromPR}
                locked={!hasPOCreatePermission}
                startIcon={<FileText />}
                module={"Procurement"}
                moduleLocked={!moduleHandle(["Procurement"])}
              />
              <Divider />
              <LockedMenuButton
                title={translate("delete")}
                disabled={isPostedOrDiscarded}
                onClick={handleOpenConfirmDelete}
                locked={!hasPRDeletePermission}
                startIcon={<Trash />}
                module={"Procurement"}
                moduleLocked={!moduleHandle(["Procurement"])}
              />
            </>
          }
        />
      </TableCell>

      <ConfirmDialog
        open={openConfirmDelete}
        title="delete"
        content="delete_purchase_requisition_confirmation"
        onClose={handleCloseConfirmDelete}
        onConfirm={handleConfirmDelete}
        confirmLoading={deleteLoading}
      />

      <ConfirmDialog
        open={openConfirmFinalize}
        title="post"
        content="post_purchase_requisition_confirmation"
        onClose={handleCloseConfirmFinalize}
        onConfirm={handleConfirmFinalize}
        confirmLoading={finalizeLoading}
        delteConfirmation={false}
        hideIcon={true}
      />
    </TableRow>
  );
};
